import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import { fetchGuestTabletSession, guestTabletEventsUrl } from "@/lib/guestTabletApi";
import { mergeGuestTabletSession } from "@/lib/guestTabletSessionMerge";
import type { GuestTabletSessionDisplay } from "@/types/guestTablet";

const SSE_RECONNECT_MS = 5_000;
/** Schnelles Polling, solange SSE getrennt ist */
const FAST_POLL_MS = 2_000;
/** Sicherheits-Polling bei aktiver SSE-Verbindung (Fallback wenn Events fehlen) */
const SAFETY_POLL_MS = 3_000;

type Options = {
  enabled?: boolean;
};

export function useGuestTabletSession(workstationCode: string, options: Options = {}) {
  const enabled = options.enabled !== false;
  const [data, setData] = useState<GuestTabletSessionDisplay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const reloadInFlightRef = useRef<Promise<void> | null>(null);

  const applySession = useCallback((session: GuestTabletSessionDisplay) => {
    setData((prev) => mergeGuestTabletSession(prev, session));
    setError(null);
    setLoading(false);
  }, []);

  const reload = useCallback(async () => {
    if (!enabled) return;
    if (reloadInFlightRef.current) {
      return reloadInFlightRef.current;
    }
    const run = (async () => {
      try {
        const session = await fetchGuestTabletSession(workstationCode);
        applySession(session);
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : "Tablet-Daten konnten nicht geladen werden.";
        setError(message);
      } finally {
        setLoading(false);
      }
    })();
    reloadInFlightRef.current = run;
    try {
      await run;
    } finally {
      reloadInFlightRef.current = null;
    }
  }, [workstationCode, applySession, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void reload();
  }, [reload, enabled]);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let fastPollTimer: number | null = null;
    let safetyPollTimer: number | null = null;

    const clearReconnect = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const stopFastPoll = () => {
      if (fastPollTimer != null) {
        window.clearInterval(fastPollTimer);
        fastPollTimer = null;
      }
    };

    const startFastPoll = () => {
      if (disposed || fastPollTimer != null) return;
      fastPollTimer = window.setInterval(() => {
        void reload();
      }, FAST_POLL_MS);
    };

    const stopSafetyPoll = () => {
      if (safetyPollTimer != null) {
        window.clearInterval(safetyPollTimer);
        safetyPollTimer = null;
      }
    };

    const startSafetyPoll = () => {
      if (disposed || safetyPollTimer != null) return;
      safetyPollTimer = window.setInterval(() => {
        void reload();
      }, SAFETY_POLL_MS);
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer != null) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, SSE_RECONNECT_MS);
    };

    const connect = () => {
      if (disposed) return;
      eventSource?.close();
      eventSource = null;

      try {
        eventSource = new EventSource(guestTabletEventsUrl(workstationCode));
      } catch {
        startFastPoll();
        scheduleReconnect();
        return;
      }

      eventSource.addEventListener("guest_tablet_session_changed", (ev) => {
        try {
          const parsed = JSON.parse((ev as MessageEvent).data) as GuestTabletSessionDisplay;
          applySession(parsed);
        } catch {
          void reload();
        }
      });

      eventSource.onopen = () => {
        stopFastPoll();
        startSafetyPoll();
        clearReconnect();
        void reload();
      };

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        stopSafetyPoll();
        startFastPoll();
        scheduleReconnect();
      };
    };

    connect();
    startFastPoll();

    return () => {
      disposed = true;
      eventSource?.close();
      clearReconnect();
      stopFastPoll();
      stopSafetyPoll();
    };
  }, [workstationCode, reload, applySession, enabled]);

  return { data, error, loading, reload, applySession };
}
